/* eslint-disable @typescript-eslint/no-explicit-any */
import { apiClient } from '../api';
import { Path } from '../api/backendUrl';
import {
  Job,
  JobStatus,
  JobThread,
  QuoteRevision,
  QuoteThreadAttachment,
  QuoteThreadMessage,
  TimeframePreference,
} from '../utils/type';

const SERVICE_ENDPOINT = import.meta.env.VITE_APP_BACKEND_SERVICE;

export interface PromptAnswer {
  promptId: string;
  label: string;
  type: string;
  answer: string | number | boolean | string[];
}

export interface CreateJobPayload {
  serviceId: string;
  scopeDescription: string;
  photos?: string[];
  location?: string;
  timeframePreference: TimeframePreference;
  budgetMin?: number;
  budgetMax?: number;
  additionalServiceIds?: string[];
  promptAnswers?: PromptAnswer[];
}

export interface ServiceRequestConfig {
  serviceId: string;
  providerId: string;
  additionalServiceIds?: string[];
  promptAnswers?: PromptAnswer[];
}

export interface CreateBroadcastJobPayload {
  // One entry per selected provider's OWN ServiceInformation listing — a
  // serviceId belongs to exactly one provider, so N selected providers means
  // N distinct { serviceId, providerId } pairs, never one shared serviceId
  // plus a providerIds list (see PR #809's fix on the pms side for why).
  services: ServiceRequestConfig[];
  scopeDescription: string;
  photos?: string[];
  location?: string;
  timeframePreference: TimeframePreference;
  budgetMin?: number;
  budgetMax?: number;
}

export interface JobsResponse {
  jobs: Job[];
  total: number;
  page: number;
  pages: number;
  limit: number;
}

/** Extract a Job from a list/detail response, tolerating envelope shapes. */
const unwrapJob = (data: any): Job =>
  data?.job ||
  data?.data?.job ||
  (data && typeof data === 'object' && 'jobId' in data ? data : data);

/**
 * POST /v1/job — customer creates a Job Request (status → 'Requested',
 * bookingType → 'QUOTE').
 */
export const createJob = async (data: CreateJobPayload): Promise<Job> => {
  const res = await apiClient.post(
    `${SERVICE_ENDPOINT}${Path.jobCreate}`,
    data
  );
  return unwrapJob(res.data);
};

/**
 * POST /v1/job/broadcast — customer creates a broadcast Quote Request to N providers
 * (status → 'Requested', bookingType → 'QUOTE'). One Job is created per provider.
 *
 * Returns the created batch with child job references.
 */
export const createBroadcastJob = async (
  data: CreateBroadcastJobPayload
): Promise<any> => {
  const idempotencyKey = crypto.randomUUID();
  const res = await apiClient.post(
    `${SERVICE_ENDPOINT}${Path.jobCreate.replace('/job', '/job/broadcast')}`,
    data,
    {
      headers: {
        'Idempotency-Key': idempotencyKey,
      },
    }
  );
  return res.data?.data || res.data;
};

/**
 * GET /v1/job/customer — my job requests (customer).
 */
export const getMyJobs = async (
  params: { page?: number; limit?: number; status?: JobStatus } = {}
): Promise<JobsResponse> => {
  const { page = 1, limit = 10, status } = params;
  const res = await apiClient.get(
    `${SERVICE_ENDPOINT}${Path.jobListCustomer}`,
    {
      params: { page, limit, ...(status ? { status } : {}) },
    }
  );
  const data = res.data;
  // BE list envelope: { success, message, items, total, page, … } — array lives
  // under `items` (Phase 2 list contract).
  const jobs =
    data?.items ||
    data?.jobs ||
    data?.data?.items ||
    data?.data?.jobs ||
    data?.data ||
    data?.results;
  const items = Array.isArray(jobs) ? jobs : Array.isArray(data) ? data : [];
  const metadata =
    data?.data && typeof data.data === 'object' ? data.data : data;
  const total =
    typeof metadata?.total === 'number' ? metadata.total : items.length;
  const responseLimit =
    typeof metadata?.limit === 'number' ? metadata.limit : limit;
  const pages =
    typeof metadata?.pages === 'number'
      ? metadata.pages
      : Math.ceil(total / responseLimit);

  return {
    jobs: items,
    total,
    page: typeof metadata?.page === 'number' ? metadata.page : page,
    pages,
    limit: responseLimit,
  };
};

/**
 * GET /v1/job/:id — job detail (customer, quote review + tracking).
 */
export const getJobById = async (jobId: string): Promise<Job> => {
  const res = await apiClient.get(
    `${SERVICE_ENDPOINT}${Path.jobDetail.replace(':jobId', jobId)}`
  );
  return unwrapJob(res.data);
};

/**
 * POST /v1/job/:id/accept — customer accepts a quote (status → 'Accepted').
 */
export const acceptJob = async (jobId: string): Promise<Job> => {
  const res = await apiClient.post(
    `${SERVICE_ENDPOINT}${Path.jobAccept.replace(':jobId', jobId)}`
  );
  return unwrapJob(res.data);
};

export interface AcceptBroadcastResult {
  batchId: string;
  status: string;
  winnerJobId: string;
  declinedCount: number;
}

/**
 * POST /v1/job/broadcast/:batchId/accept — customer accepts one quote in a
 * broadcast batch (S8 comparison view). Atomic on the backend: the chosen
 * job → 'Accepted', every other sibling still Requested/Quoted → 'Declined',
 * batch → 'RESOLVED'. Returns batch-resolution metadata only, not the
 * updated job documents — callers already have the winner's data client-side
 * (it's the column the customer just accepted) and should invalidate/refetch
 * for the rest.
 */
export const acceptBroadcastQuote = async (
  batchId: string,
  jobId: string
): Promise<AcceptBroadcastResult> => {
  const res = await apiClient.post(
    `${SERVICE_ENDPOINT}${Path.jobBroadcastAccept.replace(':batchId', batchId)}`,
    { jobId }
  );
  return res.data?.data || res.data;
};

/**
 * POST /v1/job/:id/decline — customer declines a quote (status → 'Declined',
 * terminal). `declinedNote` carries the customer's optional reason. The BE
 * stores it on the Job's `declinedNote` field only -- there's no separate
 * `declinedAt`/`declinedBy` (the existing `updatedAt`/`updatedBy` cover that).
 */
export const declineJob = async (
  jobId: string,
  declinedNote?: string
): Promise<Job> => {
  const res = await apiClient.post(
    `${SERVICE_ENDPOINT}${Path.jobDecline.replace(':jobId', jobId)}`,
    declinedNote ? { declinedNote } : {}
  );
  return unwrapJob(res.data);
};

/**
 * POST /v1/job/:id/cancel — customer cancels a quote job (parity with slot
 * bookings). `cancelledNote` carries the customer's reason; the BE records it
 * alongside `cancelledAt`/`cancelledBy`.
 */
export const cancelJob = async (
  jobId: string,
  cancelledNote?: string
): Promise<Job> => {
  const res = await apiClient.post(
    `${SERVICE_ENDPOINT}${Path.jobCancel.replace(':jobId', jobId)}`,
    cancelledNote ? { cancelledNote } : {}
  );
  return unwrapJob(res.data);
};

/**
 * POST /v1/job/:id/confirm-completion — customer confirms an Awaiting
 * (provider marked done) job. Transitions status Awaiting -> Completed and
 * sets completionConfirmedAt/By on the BE.
 */
export const confirmJobCompletion = async (jobId: string): Promise<Job> => {
  const res = await apiClient.post(
    `${SERVICE_ENDPOINT}${Path.jobConfirmCompletion.replace(':jobId', jobId)}`
  );
  return unwrapJob(res.data);
};

/**
 * POST /v1/job/:id/request-changes — customer requests changes to the
 * current quote (status 'Quoted' -> 'ChangesRequested'). Also inserts the
 * first `QuoteThreadMessage`. `attachments` must already be uploaded via
 * `uploadThreadAttachments` before calling this.
 */
export const requestQuoteChanges = async (
  jobId: string,
  message: string,
  attachments?: QuoteThreadAttachment[]
): Promise<{ job: Job; threadMessage: QuoteThreadMessage }> => {
  const res = await apiClient.post(
    `${SERVICE_ENDPOINT}${Path.jobRequestChanges.replace(':jobId', jobId)}`,
    { message, ...(attachments?.length ? { attachments } : {}) }
  );
  return { job: unwrapJob(res.data), threadMessage: res.data?.threadMessage };
};

/**
 * POST /v1/job/:id/cancel-request-changes — customer withdraws a pending
 * "Request Changes" and reverts the job back to 'Quoted', so the original
 * (still-valid) quote is actionable again (Accept / Decline / Request
 * Changes). Requires a matching BE route + status-machine transition
 * (ChangesRequested -> Quoted) — not present in the current backend.
 */
export const cancelRequestChanges = async (jobId: string): Promise<Job> => {
  const res = await apiClient.post(
    `${SERVICE_ENDPOINT}${Path.jobCancelRequestChanges.replace(':jobId', jobId)}`
  );
  return unwrapJob(res.data);
};

/**
 * GET /v1/job/:id/revisions — quote revision history, ascending by
 * `revisionNo`. Read-only, never blocked by `isNegotiable`.
 */
export const getJobRevisions = async (
  jobId: string
): Promise<QuoteRevision[]> => {
  const res = await apiClient.get(
    `${SERVICE_ENDPOINT}${Path.jobRevisions.replace(':jobId', jobId)}`
  );
  return res.data?.revisions || [];
};

/**
 * GET /v1/job/:id/thread — negotiation thread messages + lock state.
 * `thread.locked` is the server-derived `STATUS_META.isNegotiable` flag —
 * the only source of truth for whether the composer should render.
 */
export const getJobThread = async (jobId: string): Promise<JobThread> => {
  const res = await apiClient.get(
    `${SERVICE_ENDPOINT}${Path.jobThread.replace(':jobId', jobId)}`
  );
  return res.data?.thread;
};

/**
 * POST /v1/job/:id/thread/messages — post a negotiation message. `body` is
 * required unless `attachments` carries at least one file (mirrors the BE's
 * `postThreadMessageRules`).
 */
export const postThreadMessage = async (
  jobId: string,
  payload: {
    body?: string;
    attachments?: QuoteThreadAttachment[];
    revisionNo?: number;
  }
): Promise<QuoteThreadMessage> => {
  const res = await apiClient.post(
    `${SERVICE_ENDPOINT}${Path.jobThreadMessages.replace(':jobId', jobId)}`,
    payload
  );
  return res.data?.threadMessage;
};

/**
 * POST /v1/job/:id/thread/attachments — upload negotiation attachments
 * (multipart, field `files`, max 5). Client mirrors the server's 5MB/file,
 * image+PDF-only limits for UX only; the server is authoritative and
 * re-validates (MIME allowlist + PDF magic-byte check) regardless of what's
 * enforced here.
 */
export const uploadThreadAttachments = async (
  jobId: string,
  files: File[]
): Promise<QuoteThreadAttachment[]> => {
  const formData = new FormData();
  files.forEach((file) => formData.append('files', file));

  const res = await apiClient.post(
    `${SERVICE_ENDPOINT}${Path.jobThreadAttachments.replace(':jobId', jobId)}`,
    formData,
    {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 60000,
    }
  );
  return res.data?.attachments || [];
};
